import React, { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { audioEngine } from '../services/audioEngine';
import { API_BASE, AUTH_TOKEN, IS_GUEST } from '../config';
import { api } from '../services/api';

// PremiereMoment — the payoff (Build_Game_Intro §4.6).
//
// They walk out. The DJ premieres the track on air — heard on the street.
// Radio doesn't show artwork, so nothing needs approving mid-scene and the
// emotional beat runs uninterrupted; the first play precedes the drop, which
// is how records actually work.
//
// Armed by the session (release consent → store.premiere), fired by the door:
// the moment the player steps from the studio interior onto the street, their
// record takes the city's audio — audioEngine.playTrack stops the station,
// plays it LOUD, and announces it on the now-playing ticker, so the world's
// audio-reactive layer responds to THEIR song. An ON AIR card carries the
// premiere framing; the walk-out also reports the drop quest's goto objective,
// because stepping outside IS Act 4's objective.
//
// Diegetic music rule: the song does not have to match the mood of the street.
// Radio plays what radio plays — that's the point.

export default function PremiereMoment() {
  const premiere = useWorldStore((s) => s.premiere);
  const interior = useWorldStore((s) => s.interior);
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const [card, setCard] = useState(null);
  const firedRef = useRef(null);   // trackId already premiered — once, ever

  useEffect(() => {
    if (!premiere || interior) return undefined;           // armed, still inside
    if (firedRef.current === premiere.trackId) return undefined;
    firedRef.current = premiere.trackId;

    // ── ON AIR ──────────────────────────────────────────────────────────────
    // A premiere armed from a record that was ALREADY out (a plaque, or "it's
    // already released") can arrive without its audio URL. It used to show the
    // ON AIR card over silence; now the track is looked up in the artist's own
    // catalogue (a read) and plays like any other.
    const onAir = (url) => audioEngine.playTrack({
      track_id: premiere.trackId,
      title: premiere.title || 'Your record',
      audio_url: url,
      avatar_name: 'World premiere',
    });
    if (premiere.audioUrl) onAir(premiere.audioUrl);
    else if (premiere.trackId && (premiere.avatarId || myAvatarId)) {
      api.avatarTracks(premiere.avatarId || myAvatarId)
        .then((d) => {
          const t = (d?.tracks || []).find((x) => String(x.track_id) === String(premiere.trackId));
          if (t?.audio_url) onAir(t.audio_url);
        })
        .catch(() => { /* the card still carries the moment */ });
    }
    setCard({
      title: premiere.title || 'Your record',
      station: premiere.stations?.[0]?.name || 'HypeTown Radio',
    });

    // The card holds long enough to be believed, then the street is just a
    // street with your song on it. The premiere itself keeps playing.
    const t = setTimeout(() => {
      setCard(null);
      useWorldStore.getState().setPremiere(null);
    }, 14000);
    return () => clearTimeout(t);
  }, [premiere, interior, myAvatarId]);

  // ── ACT 4: WALKING OUT OF THE STUDIO ─────────────────────────────────────
  // The drop quest's one objective is `goto street_studio_exit` — stepping back
  // onto the street after the session. It was only reported from inside the
  // premiere, which broke two ways: the quest was never STARTED (so the server
  // answered "Quest not started" to both calls, silently), and a player who said
  // "not yet" to releasing never got a premiere and so could never finish Act 4.
  //
  // Now the exit itself reports it, premiere or not, whenever the drop quest is
  // actually open — starting it first if it is only available. A quest absent
  // from the phone's snapshot is either done or still locked, and is left alone
  // (starting a completed quest re-opens it server-side).
  const prevInterior = useRef(interior);
  useEffect(() => {
    const was = prevInterior.current;
    prevInterior.current = interior;
    if (IS_GUEST || !myAvatarId || was !== 'studio' || interior) return;
    const q = (useWorldStore.getState().phoneQuests || []).find((x) => x.slug === 'come_up_the_drop');
    if (!q || (q.status !== 'available' && q.status !== 'active')) return;
    const call = (path, body) => fetch(`${API_BASE}/api/quests/come_up_the_drop/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${AUTH_TOKEN}` },
      body: JSON.stringify({ avatarId: myAvatarId, ...(body || {}) }),
    });
    (async () => {
      try {
        if (q.status === 'available') await call('start');
        await call('progress', { objectiveType: 'goto', targetRef: 'street_studio_exit' });
        await call('sync');
        // What comes next (the handoff call) is decided by the quest list.
        useWorldStore.getState().refreshQuests?.();
      } catch { /* quests catch up on the next sync */ }
    })();
  }, [interior, myAvatarId]);

  if (!card) return null;

  return (
    <div style={wrap}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
        <span style={dot} />
        <span style={{ font: '800 11px system-ui', letterSpacing: 2, color: '#ff8a9c' }}>ON AIR · FIRST PLAY</span>
      </div>
      <div style={{ font: '800 20px system-ui', margin: '6px 0 2px' }}>“{card.title}”</div>
      <div style={{ font: '600 11.5px system-ui', opacity: 0.65 }}>{card.station} · the city is listening</div>
    </div>
  );
}

const wrap = {
  position: 'fixed', left: '50%', transform: 'translateX(-50%)',
  top: 'max(56px, calc(env(safe-area-inset-top) + 42px))', zIndex: 72,
  padding: '14px 26px', borderRadius: 18, textAlign: 'center', pointerEvents: 'none',
  color: '#ffe9c4', border: '1px solid rgba(255,138,156,0.5)',
  background: 'rgba(14,9,6,0.92)',
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  boxShadow: '0 20px 60px rgba(0,0,0,0.55)',
};
const dot = {
  width: 8, height: 8, borderRadius: '50%', background: '#ff5e78',
  boxShadow: '0 0 10px #ff5e78',
};
